import { Box, Card, CardContent, Stack, useMediaQuery, useTheme } from '@mui/material';
import { isValid } from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';

import HealthRings, { HealthRingsDataPoint } from '@/components/common/HealthRings';
import RadarChart, { RadarChartDataSeries } from '@/components/common/RadarChart';
import {
  COLORS,
  CTX_RADAR_TOOLTIPS,
  ScoreLockup,
  isHiddenSubscore,
  LoadingRadarChart,
  MobileCategoryBars,
  SCORE_TOOLTIPS,
  ShimmerValue,
  Tip,
} from '@/components/dashboard/scoring';
import { CardMode } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import { parseDate } from '@/utils/formatDate';

export interface ContextScoreCardProps {
  cardMode?: CardMode;
  nhdEnabled?: boolean;
  onNhdToggle?: () => void;
  footer?: React.ReactNode;
  /** 27": with a footer, fill the height it's given; the footer (rows) scrolls inside the card. */
  fill?: boolean;
  /** The Area on a computer: axis labels carry their subscores, standing in for the category rows */
  axisValues?: boolean;
}


// Spider graph display order (clockwise from top):
// Risk pillars occupy the upper half; location pillars occupy the lower half.
const CONTEXT_RADAR_AXES = [
  { label: 'Hazards', aliases: ['hazards', 'natural hazards'], tooltipKey: 'NATURAL_HAZARDS' },
  { label: 'Safety', aliases: ['safety'], tooltipKey: 'SAFETY' },
  { label: 'Access', aliases: ['access', 'location'], tooltipKey: 'LOCATION' },
  { label: 'Parcel', aliases: ['site', 'parcel'], tooltipKey: 'SITE' },
  { label: 'Amenities', aliases: ['amenities'], tooltipKey: 'AMENITIES' },
  { label: 'Health', aliases: ['health', 'health & quality'], tooltipKey: 'HEALTH_QUALITY' },
];
// All known Context subscores. The visible set filters out anything in
// HIDDEN_SUBSCORES so a metric we've decided not to ship for launch
// doesn't appear in the radar, the score breakdown, or the legend.
const ALL_CONTEXT_SUBSCORES = ['LocationQuality', 'RiskExposure'];
const CONTEXT_SUBSCORE_ORDER = ALL_CONTEXT_SUBSCORES.filter(
  (s) => !isHiddenSubscore(s),
);
const CONTEXT_CATEGORIES = CONTEXT_RADAR_AXES.map(({ label }) => label.toUpperCase());
const CONTEXT_RADAR_LABEL_TOOLTIPS = Object.fromEntries(
  CONTEXT_RADAR_AXES.map(({ label, tooltipKey }) => [
    label.toUpperCase(),
    CTX_RADAR_TOOLTIPS[tooltipKey],
  ]),
);

const scoreToColor = (s: number): string =>
  s >= 86 ? '#f3f3f3' : s >= 77 ? '#5AE9FF' : s >= 68 ? COLORS.moderate : s >= 59 ? COLORS.urgent : COLORS.critical;

type VizMode = 'bars' | 'spider' | 'rings';

const MOBILE_VIZ_SIZE = 245 * 1.3;
const MOBILE_RINGS_SIZE = MOBILE_VIZ_SIZE * 0.9;

const ContextScoreCard = ({
  cardMode: _cardMode = 'light',
  nhdEnabled,
  onNhdToggle,
  footer,
  fill = false,
  axisValues = false,
}: ContextScoreCardProps) => {
  const [vizMode, setVizMode] = useState<VizMode>('spider');
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tooltipContainerRef = useRef<HTMLDivElement>(null);

  const cardBgColor = COLORS.card;

  const {
    contextRadarData,
    contextScore,
    contextTabs: _contextTabs,
    isLoading,
    contextScoredAt,
  } = useHauserEQResults(propertyId);
  const { scoreToGrade } = useScoringConfig();

  const score = contextScore;
  // Reorder radar data to match desired spider graph layout by label
  const radarData = useMemo(() => {
    if (!contextRadarData) return null;
    const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '');
    const byKey = Object.fromEntries(contextRadarData.map((d) => [norm(d.label), d]));
    const ordered = CONTEXT_RADAR_AXES.map(({ aliases }) => (
      aliases.map((alias) => byKey[norm(alias)]).find(Boolean)
    ));

    // A pre-v2 score contains HAZARDS/NEIGHBORHOOD/HISTORY/MARKET. Never
    // duplicate one of those values into missing six-pillar axes: that produces
    // a plausible-looking but false radar. The property must be rescored first.
    return ordered.every((point) => point != null)
      ? ordered.map((point, index) => ({
        ...point,
        label: CONTEXT_RADAR_AXES[index].label,
      }))
      : null;
  }, [contextRadarData]);
  const _tabs = useMemo(() => {
    if (!_contextTabs) return _contextTabs;
    const rank = (id: string) => {
      const i = CONTEXT_SUBSCORE_ORDER.indexOf(id);
      return i === -1 ? CONTEXT_SUBSCORE_ORDER.length : i;
    };
    return [..._contextTabs]
      .filter((t) => !isHiddenSubscore(t.id))
      .sort((a, b) => rank(a.id) - rank(b.id));
  }, [_contextTabs]);

  const hasData = score != null;
  const grade = hasData ? scoreToGrade(score) : null;

  const series: RadarChartDataSeries[] = useMemo(() => {
    if (!radarData) return [];
    const segmentColors = radarData.map((point) => scoreToColor(point.value));
    return [
      {
        data: radarData,
        fillColor: 'var(--tx-fill)',
        strokeWidth: 3,
        segmentColors,
      },
    ];
  }, [radarData]);

  const healthRingsData: HealthRingsDataPoint[] = useMemo(() => {
    if (!radarData) return [];
    return radarData.map((point, i) => ({
      label: CONTEXT_CATEGORIES[i] ?? point.label,
      value: point.value,
      color: scoreToColor(point.value),
    }));
  }, [radarData]);

  // Build mobile category bar data from radar data
  const mobileCategoryData = useMemo(() => {
    if (!radarData) return [];
    return radarData.map((point, i) => ({
      label: CONTEXT_CATEGORIES[i] ?? point.label,
      score: Math.round(point.value),
    }));
  }, [radarData]);

  if (!propertyId || isLoading) {
    return (
      <Box sx={{ height: footer ? 'auto' : '100%' }}>
        <Card
          sx={{
            bgcolor: cardBgColor,
            borderRadius: '24px',
            minHeight: isMobile || !footer ? 'auto' : 660,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <CardContent sx={{ p: '20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <span style={{ fontSize: 17, fontWeight: 600, color: COLORS.text, opacity: 0.5 }}>
                  The Area
                </span>
                <div style={{ textAlign: 'right' }}>
                  <ShimmerValue width={72} height={60} borderRadius={8} />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 8,
                      marginTop: 4,
                    }}>
                    <span style={{ fontSize: 17, color: COLORS.textDim, opacity: 0.4 }}>/100</span>
                    <ShimmerValue width={32} height={22} borderRadius={10} />
                  </div>
                </div>
              </Stack>
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}>
                  <LoadingRadarChart
                    labels={['Hazards', 'Safety', 'Access', 'Parcel', 'Amenities', 'Health']}
                    accentColor={COLORS.accent1}
                    size={550}
                  />
                </Box>
              )}
              {/* Subscore shimmer hidden — subscores not displayed until further notice */}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!isLoading && !hasData) {
    return (
      <Box>
        <Card
          sx={{
            bgcolor: cardBgColor,
            borderRadius: '24px',
            minHeight: isMobile ? 'auto' : 660,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <CardContent
            sx={{
              p: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? 200 : 620,
              flex: 1,
            }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: '#494949', marginBottom: 10 }}>
                The Area
              </span>
              <span style={{ fontSize: 14, color: '#494949' }}>Data not available</span>
            </div>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    // a stable view-transition name: moving between Overview and this card's own page, the card glides into place
    <Box data-testid="context-score-card" sx={{ height: footer && !fill ? 'auto' : '100%', viewTransitionName: 'hx-area-card' }}>
      <Card
        sx={{
          bgcolor: cardBgColor,
          borderRadius: '24px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <CardContent
          ref={tooltipContainerRef}
          sx={{
            p: '20px',
            '&:last-child': { pb: footer ? '0px' : '20px' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: footer && !fill ? 'auto' : isMobile ? 'auto' : 0,
            position: 'relative',
            flex: 1,
          }}>
          <Stack spacing={2} sx={{ flex: 1, minHeight: fill ? 0 : undefined }}>
            {/* Header: Title + NHD toggle (left) + Score + /100 + Grade (right) */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ position: 'relative', zIndex: 3, pointerEvents: 'none' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 12,
                  flexDirection: isMobile ? 'column' : 'row',
                  pointerEvents: 'auto',
                }}>
                {/* 27" (fill): the same reserved title height as The Home's, so both radars sit at the same y */}
                <div style={{ minHeight: fill ? 72 : undefined }}>
                  <Tip text={SCORE_TOOLTIPS.ContextScore}>
                    <span
                      style={{ fontSize: 22, fontWeight: 300, color: COLORS.text, cursor: 'help' }}>
                      The Area
                    </span>
                  </Tip>
                  {contextScoredAt && isValid(parseDate(contextScoredAt)) && (
                    <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                      Last scored{' '}
                      {parseDate(contextScoredAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </div>
                  )}
                </div>
                {onNhdToggle && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      onClick={onNhdToggle}
                      style={{
                        width: 40,
                        height: 22,
                        borderRadius: 11,
                        background: nhdEnabled ? COLORS.nhdAccent : 'var(--tx-fill-3)',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                        flexShrink: 0,
                      }}>
                      <div
                        style={{
                          position: 'absolute',
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: 'var(--tx-glass-card)',
                          top: 3,
                          left: nhdEnabled ? 21 : 3,
                          transition: 'left 0.2s ease',
                        }}
                      />
                    </div>
                    <Tip
                      text={
                        'Toggle the Natural Hazard Disclosure overlay. NHD adjusts scores based on ' +
                        'confirmed fire zones, flood zones, seismic hazards, special taxes, and more.'
                      }>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: nhdEnabled ? COLORS.nhdAccent : COLORS.textDim,
                          letterSpacing: 1,
                          cursor: 'help',
                        }}>
                        NHD
                      </span>
                    </Tip>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', pointerEvents: 'auto' }}>
                <ScoreLockup score={score as number} grade={grade as string} color={nhdEnabled ? COLORS.nhdAccent : 'var(--tx-text)'} compact={isMobile} />
                {
                  // Always mounted (reserves its space) so the header is the same
                  // height whether or not footer is present — otherwise the chart
                  // box below sits at a different vertical position depending on
                  // which page rendered this card.
                  <div style={{ display: footer || isMobile ? 'flex' : 'none', justifyContent: 'flex-end', marginTop: 10 }}>
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 3, gap: 2 }}>
                      {(isMobile ? (['spider', 'bars', 'rings'] as const) : (['spider', 'rings'] as const)).map((mode) => {
                        const active = vizMode === mode;
                        let icon: React.ReactNode;
                        if (mode === 'bars') {
                          const c1 = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const c2 = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const c3 = active ? '#DB5282' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <rect x="1.5" y="7" width="2.4" height="5.5" rx="0.6" fill={c1} />
                              <rect x="5.8" y="3.5" width="2.4" height="9" rx="0.6" fill={c2} />
                              <rect x="10.1" y="5.5" width="2.4" height="7" rx="0.6" fill={c3} />
                            </svg>
                          );
                        } else if (mode === 'spider') {
                          const outerC  = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const middleC = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const innerC  = active ? '#FF9A4D' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <polygon points="7,1.5 11.8,4.25 11.8,9.75 7,12.5 2.2,9.75 2.2,4.25" stroke={outerC} strokeWidth="0.9" fill="none"/>
                              <polygon points="7,3.8 10,5.5 10,8.5 7,10.2 4,8.5 4,5.5" stroke={middleC} strokeWidth="0.9" fill="none"/>
                              <polygon points="7,5.5 8.6,6.4 8.6,7.6 7,8.5 5.4,7.6 5.4,6.4" stroke={innerC} strokeWidth="0.9" fill="none"/>
                            </svg>
                          );
                        } else {
                          const outerC  = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const middleC = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const innerC  = active ? '#DB5282' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <circle cx="7" cy="7" r="5.5" stroke={outerC} strokeWidth="1"/>
                              <circle cx="7" cy="7" r="3.3" stroke={middleC} strokeWidth="1"/>
                              <circle cx="7" cy="7" r="1.2" stroke={innerC} strokeWidth="1"/>
                            </svg>
                          );
                        }
                        return (
                          <div
                            key={mode}
                            onClick={() => setVizMode(mode)}
                            style={{
                              padding: 5, borderRadius: 7, cursor: 'pointer',
                              background: active ? 'var(--tx-fill-3)' : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'background 0.15s', userSelect: 'none',
                            }}>
                            {icon}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
              </div>
            </Stack>

            {/* Mobile: toggleable bars/radar/rings | Desktop: Radar / Rings */}
            {isMobile ? (
              // Mobile score card — toggle between bars / radar / rings. Fixed height (not
              // minHeight) matching radar's own box size, and rings shrunk to the same box
              // size, so the container never resizes when switching visualizations.
              // mx cancels CardContent's 20px padding (same full-bleed trick the footer below uses).
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  height: MOBILE_VIZ_SIZE,
                  mx: '-20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                {vizMode === 'bars' && (
                  <Box sx={{ width: '100%', px: '20px' }}>
                    <MobileCategoryBars categories={mobileCategoryData} />
                  </Box>
                )}
                {vizMode === 'spider' && (
                  // RadarChart widens its radius fraction to 0.46 when showLabels is false,
                  // reclaiming the label margin, so this fills its 245px box.
                  // translateY compensates RadarChart's own asymmetric -45px/-10px vertical
                  // margins, which otherwise pull its visual center 17.5px above HealthRings'.
                  <Box sx={{ transform: 'translateY(17.5px)' }}>
                    <RadarChart
                      series={series}
                      size={MOBILE_VIZ_SIZE}
                      gridColor="rgba(255,255,255,0.1)"
                      labelColor={COLORS.textDim}
                      axisLabelColor="#BBB7AF"
                      gridLevels={4}
                      showLabels={false}
                      showValueTooltips
                      labelTooltips={CONTEXT_RADAR_LABEL_TOOLTIPS}
                      tooltipContainerRef={tooltipContainerRef}
                    />
                  </Box>
                )}
                {vizMode === 'rings' && (
                  // Slightly smaller than the radar within the fixed visualization footprint.
                  <HealthRings data={healthRingsData} size={MOBILE_RINGS_SIZE} />
                )}
              </Box>
            ) : footer ? (
              // Fixed-height container with both charts overlaid — footer stays locked. The same 420 stage, radius and
              // facets as The Home's card, so the two radars are identical when you move between the pages
              <Box sx={{ position: 'relative', zIndex: 2, height: fill ? undefined : 420, minHeight: fill ? 420 : undefined, flex: fill ? 1 : undefined }}>
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  opacity: vizMode === 'spider' ? 1 : 0,
                  pointerEvents: vizMode === 'spider' ? 'auto' : 'none',
                  transition: 'opacity 0.2s',
                }}>
                  <RadarChart
                    series={series}
                    size={550}
                    radiusFraction={0.37}
                    contoured
                    gridColor="rgba(255,255,255,0.1)"
                    labelColor={COLORS.textDim}
                    axisLabelColor="#BBB7AF"
                    gridLevels={4}
                    labelTooltips={CONTEXT_RADAR_LABEL_TOOLTIPS}
                    tooltipContainerRef={tooltipContainerRef}
                  />
                </Box>
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  opacity: vizMode === 'rings' ? 1 : 0,
                  pointerEvents: vizMode === 'rings' ? 'auto' : 'none',
                  transition: 'opacity 0.2s',
                }}>
                  <HealthRings data={healthRingsData} size={365} />
                </Box>
              </Box>
            ) : (
              // No footer (Overview): the chart takes all the height the card is given and the radar fills it, sized by
              // whichever of the box's width or height runs out first (container units). The radar's own -45px top margin
              // lets it rise into the header's empty right side, so it may be 55px taller than the box.
              <Box sx={{ flex: 1, minHeight: 0, position: 'relative', zIndex: 2, containerType: 'size' }}>
                <Box
                  sx={{
                    width: 'min(100cqw, calc(100cqh + 55px))',
                    mx: 'auto',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <RadarChart
                    series={series}
                    size={640}
                    radiusFraction={axisValues ? 0.33 : 0.37}
                    axisValues={axisValues}
                    contoured
                    gridColor="rgba(255,255,255,0.1)"
                    labelColor={COLORS.textDim}
                    axisLabelColor="#BBB7AF"
                    gridLevels={4}
                    labelTooltips={CONTEXT_RADAR_LABEL_TOOLTIPS}
                    tooltipContainerRef={tooltipContainerRef}
                  />
                </Box>
              </Box>
            )}

          </Stack>

          {footer && (
            <Box
              sx={{
                mx: '-20px',
                mt: isMobile ? '-30px' : fill ? 1 : 0,
                ...(fill && {
                  flex: 'none',
                  maxHeight: '45%',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }),
              }}>
              {footer}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContextScoreCard;
